import { ArrowBack } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Container,
    LinearProgress,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../allauth/auth';
import HighestSubscriptionTier from '../components/HighestSubscriptionTier';
import ManageAssignmentView from '../components/manage-assignment/ManageAssignmentView';
import ApiClient from '../config/ApiClient';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { PaymentAssignments } from '../object-actions/types/types';
import { getProduct } from '../subscriptions/catalog';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';
import type { ProductId } from '../subscriptions/types';
import { canEditAssignment } from '../utils/assignmentPermissions';
import { formatAssignmentPaymentDate } from '../utils/paymentAssignmentDisplay';

const ManagePaymentAssignment: React.FC = () => {
    const { assignment_id } = useParams<{ assignment_id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const auth = useAuth();
    const userId = auth?.data?.user?.id;

    const [assignment, setAssignment] = useState<PaymentAssignments | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [checkoutNotice, setCheckoutNotice] = useState<{
        severity: 'success' | 'error' | 'info';
        message: string;
        instruction?: string;
        retryable?: boolean;
    } | null>(null);

    const { confirmCheckout, loading: checkoutLoading, error: confirmError, clearError: clearConfirmError } =
        useSubscriptionCheckout();
    const confirmAttempted = useRef<string | null>(null);
    const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

    const assignmentId = assignment_id ? parseInt(assignment_id, 10) : NaN;
    const checkoutStatus = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id') ?? pendingSessionId;

    const isPayer = useMemo(() => {
        if (!assignment || !userId) return false;
        return (
            assignment.payment?.entity?.author?.id === userId ||
            assignment.author?.id === userId
        );
    }, [assignment, userId]);

    const fetchAssignment = useCallback(async () => {
        if (!assignment_id || Number.isNaN(assignmentId)) {
            setError('Assignment ID is required');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                manage: 'true',
                subfields: 'email',
            });
            const response = await ApiClient.get<PaymentAssignments>(
                `/api/payment-assignments/${assignmentId}/?${params.toString()}`,
            );

            if (response.error) {
                setError(
                    typeof response.error === 'string'
                        ? response.error
                        : 'Failed to load assignment',
                );
                setAssignment(null);
            } else if (response.success && response.data) {
                setAssignment({
                    ...response.data,
                    entitlements: normalizeEntitlementsMap(response.data.entitlements),
                });
            } else {
                setError('Assignment not found');
                setAssignment(null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load assignment');
            setAssignment(null);
        } finally {
            setLoading(false);
        }
    }, [assignmentId, assignment_id]);

    const clearCheckoutQuery = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('checkout');
        next.delete('session_id');
        next.delete('return_to');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        fetchAssignment();
    }, [fetchAssignment]);

    useEffect(() => {
        if (checkoutStatus === 'cancelled') {
            setCheckoutNotice({
                severity: 'info',
                message: 'Checkout was cancelled.',
                instruction: 'You can start a new purchase when you are ready.',
            });
            clearCheckoutQuery();
        }
    }, [checkoutStatus, clearCheckoutQuery]);

    useEffect(() => {
        if (searchParams.get('session_id')) {
            setPendingSessionId(searchParams.get('session_id'));
        }
    }, [searchParams]);

    // Per-product upgrade confirmation: an upgrade purchase routes back here
    // with `?upgraded=<product>` so we can surface tailored copy on the hub.
    useEffect(() => {
        const upgraded = searchParams.get('upgraded') as ProductId | null;
        if (!upgraded) {
            return;
        }
        const product = getProduct(upgraded);
        setCheckoutNotice({
            severity: 'success',
            message: product
                ? `${product.name} is now unlocked on this assignment.`
                : 'Your upgrade is now unlocked on this assignment.',
        });
        const next = new URLSearchParams(searchParams);
        next.delete('upgraded');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const runConfirm = useCallback(
        async (stripeSessionId: string) => {
            const returnTo = searchParams.get('return_to');
            clearConfirmError();
            const result = await confirmCheckout(stripeSessionId);
            clearCheckoutQuery();
            setPendingSessionId(stripeSessionId);

            if (result) {
                setAssignment((prev) =>
                    prev ? { ...prev, entitlements: result.entitlements } : prev,
                );
                if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
                    navigate(returnTo, {
                        replace: true,
                        state: {
                            checkoutSuccess: result.productName,
                            alreadyFulfilled: result.alreadyFulfilled,
                        },
                    });
                    return;
                }
                setCheckoutNotice({
                    severity: 'success',
                    message: result.alreadyFulfilled
                        ? `${result.productName} is already active on this assignment.`
                        : `Purchase complete — ${result.productName} is now unlocked.`,
                });
            }
        },
        [clearCheckoutQuery, clearConfirmError, confirmCheckout, navigate, searchParams],
    );

    useEffect(() => {
        const urlSessionId = searchParams.get('session_id');
        if (checkoutStatus !== 'success' || !urlSessionId || confirmAttempted.current === urlSessionId) {
            return;
        }
        confirmAttempted.current = urlSessionId;
        void runConfirm(urlSessionId);
    }, [checkoutStatus, searchParams, runConfirm]);

    useEffect(() => {
        if (confirmError) {
            setCheckoutNotice({
                severity: 'error',
                message: confirmError.message,
                instruction: confirmError.instruction,
                retryable: confirmError.retryable,
            });
        }
    }, [confirmError]);

    const handleRetryConfirm = () => {
        if (!sessionId) return;
        setCheckoutNotice(null);
        confirmAttempted.current = null;
        void runConfirm(sessionId);
    };

    const handleClose = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/account/payments');
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    if (error || !assignment) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Button
                    component={Link}
                    to="/account/payments"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 2 }}
                >
                    Back to Program Assignments
                </Button>
                <Alert severity="error">{error || 'Assignment not found'}</Alert>
            </Container>
        );
    }

    const userRole = isPayer ? 'payer' : (
        assignment.payment?.entity?.author?.id === userId ? 'payer' : null
    );

    if (!canEditAssignment(userId, assignment, userRole)) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Button
                    component={Link}
                    to="/account/payments"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 2 }}
                >
                    Back to Program Assignments
                </Button>
                <Alert severity="warning">
                    You do not have permission to manage this assignment.
                </Alert>
            </Container>
        );
    }

    const paymentDate = formatAssignmentPaymentDate(assignment);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                    onClick={handleClose}
                    startIcon={<ArrowBack />}
                    size="small"
                    data-attr="manage-payment-assignment_back"
                >
                    Back
                </Button>
                <Typography variant="body2" color="text.secondary">
                    Assignment #{assignment.id}
                    {paymentDate ? ` · ${paymentDate}` : ''}
                </Typography>
            </Box>

            {checkoutLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Confirming your payment…
                </Alert>
            )}

            {checkoutNotice && (
                <Alert
                    severity={checkoutNotice.severity}
                    sx={{ mb: 2 }}
                    action={
                        checkoutNotice.retryable && sessionId ? (
                            <Button color="inherit" size="small" onClick={handleRetryConfirm}>
                                Try again
                            </Button>
                        ) : undefined
                    }
                    onClose={() => setCheckoutNotice(null)}
                >
                    <Typography variant="body2">{checkoutNotice.message}</Typography>
                    {checkoutNotice.instruction && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {checkoutNotice.instruction}
                        </Typography>
                    )}
                </Alert>
            )}

            <HighestSubscriptionTier entitlements={assignment.entitlements} />

            <ManageAssignmentView
                onClose={handleClose}
                onSuccess={fetchAssignment}
                assignment={assignment}
                isPayer={isPayer}
            />
        </Container>
    );
};

export default ManagePaymentAssignment;
